import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ShoppingCart, CheckCircle2, Circle, Trash2, Plus, X, Search, ClipboardList, Loader2, Sparkles, AlertCircle, ChevronUp, ChevronDown, Check, Bot, Utensils, Camera, Link as LinkIcon, FileText, Send, BookOpen, Clock, Flame, ShieldAlert, QrCode, ExternalLink, RefreshCw, History, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { GroceryItem, Language, DeficiencyAlert, Vibe, SubscriptionTier } from '../types';
import { StoreLocator } from './StoreLocator';
import { NutriAgentConfigModal, NutriAgentLaunchConfig, GroceryListMode } from './NutriAgentConfigModal';
import { NutriAgentProgress } from './NutriAgentProgress';
import { NutriAgentApprovalModal } from './NutriAgentApprovalModal';
import { NutriAgentOrderDetailModal, NutriAgentOrderRecord } from './NutriAgentOrderDetailModal';
import { analyzeRestaurantMenu, analyzeRestaurantMenuText, parseRecipeFromUrl, MenuRecommendation, RecipeDetails } from '../services/nutritionService';
import { resizeImage } from '../lib/imageUtils';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

interface AiConciergeProps {
  items: GroceryItem[];
  onToggleItem: (id: string) => void;
  onDeleteItem: (id: string) => void;
  onClearChecked: () => void;
  onAddItem: (name: string, amount?: string, unit?: string) => void;
  onAddMultipleItems?: (items: Array<{ name: string; amount?: string; unit?: string; source_meal_id?: string }>) => void;
  plannedMeals?: any[];
  activeDeficiencies?: DeficiencyAlert[];
  lang: Language;
  vibe?: Vibe | 'casual' | 'professional';
  theme: 'light' | 'dark';
  illumination?: boolean;
  onFetchSuggestions?: () => Promise<void>;
  isGeneratingSuggestions?: boolean;
  skin?: 'default' | 'cyberpunk' | 'anime' | 'stalker';
  userTier?: SubscriptionTier;
  onOpenSubscriptionModal?: () => void;
}

export const AiConcierge: React.FC<AiConciergeProps> = ({
  items,
  onToggleItem,
  onDeleteItem,
  onClearChecked,
  onAddItem,
  onAddMultipleItems,
  plannedMeals = [],
  activeDeficiencies = [],
  lang,
  vibe = 'casual',
  theme,
  illumination,
  onFetchSuggestions,
  isGeneratingSuggestions = false,
  skin = 'default',
  userTier = 'free',
  onOpenSubscriptionModal
}) => {
  const [activeTab, setActiveTab] = useState<'groceries' | 'eating_out' | 'recipes'>('groceries');
  const [recipeUrl, setRecipeUrl] = useState('');
  const [isParsingRecipe, setIsParsingRecipe] = useState(false);
  const [parsedRecipe, setParsedRecipe] = useState<RecipeDetails | null>(null);
  const [expandedRecipes, setExpandedRecipes] = useState<Set<number>>(new Set());
  const [recipeHistory, setRecipeHistory] = useState<RecipeDetails[]>([]);
  const [nutriAgentHistory, setNutriAgentHistory] = useState<any[]>([]);
  const [openRecipes, setOpenRecipes] = useState<Record<string, boolean>>({});
  const [isDeficienciesExpanded, setIsDeficienciesExpanded] = useState(false);

  const longPressTimer = useRef<any>(null);
  const isLongPressActive = useRef(false);
  const startPos = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    import("../services/localDbService").then(({ localDb }) => {
      localDb.getItem("recipe_history").then(val => {
        if (val) setRecipeHistory(JSON.parse(val));
      });
      localDb.getItem("nutri_agent_history").then(val => {
        if (val) setNutriAgentHistory(JSON.parse(val));
      });
    });
  }, []);

  const toggleRecipeOpen = (key: string) => {
    setOpenRecipes(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handlePointerDown = (e: React.PointerEvent, id: string, onDelete: () => void) => {
    if (e.button !== 0) return;
    startPos.current = { x: e.clientX, y: e.clientY };
    isLongPressActive.current = false;
    
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
    
    longPressTimer.current = setTimeout(() => {
      isLongPressActive.current = true;
      import("../lib/haptic").then(({ triggerHaptic }) => triggerHaptic('SUCCESS'));
      onDelete();
    }, 700);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!startPos.current || !longPressTimer.current) return;
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > 10) {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }
  };

  const handlePointerUp = (e: React.PointerEvent, onClick: () => void) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    startPos.current = null;
    
    if (isLongPressActive.current) {
      e.preventDefault();
      e.stopPropagation();
      isLongPressActive.current = false;
      return;
    }
    onClick();
  };

  const handlePointerCancel = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    startPos.current = null;
    isLongPressActive.current = false;
  };


  // Dining Out / Menu Scan State
  const menuFileInputRef = useRef<HTMLInputElement>(null);
  const qrFileInputRef = useRef<HTMLInputElement>(null);
  const [menuTextQuery, setMenuTextQuery] = useState('');
  const [qrMenuUrl, setQrMenuUrl] = useState('');
  const [isAnalyzingMenu, setIsAnalyzingMenu] = useState(false);
  const [menuRecommendations, setMenuRecommendations] = useState<MenuRecommendation[]>([]);
  const [scannedMenuImage, setScannedMenuImage] = useState<string | null>(null);
  const [isLiveQrActive, setIsLiveQrActive] = useState(false);
  const liveQrScannerRef = useRef<any>(null);

  const handleToggleLiveQrScanner = async () => {
    if (isLiveQrActive) {
      if (liveQrScannerRef.current) {
        try {
          await liveQrScannerRef.current.stop();
          liveQrScannerRef.current.clear();
        } catch (e) {
          console.error("Error stopping live QR scanner:", e);
        }
        liveQrScannerRef.current = null;
      }
      setIsLiveQrActive(false);
    } else {
      setIsLiveQrActive(true);
      setTimeout(async () => {
        try {
          const container = document.getElementById("dining-qr-scanner");
          if (!container) return;
          const { Html5Qrcode } = await import('html5-qrcode');
          const html5QrCode = new Html5Qrcode("dining-qr-scanner");
          liveQrScannerRef.current = html5QrCode;

          await html5QrCode.start(
            { facingMode: "environment" },
            { fps: 15 },
            async (decodedText) => {
              if (decodedText) {
                toast.success(lang === 'ua' ? 'QR-код мену розпізнано! 🔗' : 'Menu QR Code Scanned! 🔗');
                try {
                  await html5QrCode.stop();
                  html5QrCode.clear();
                } catch (e) {}
                setIsLiveQrActive(false);
                liveQrScannerRef.current = null;

                if (decodedText.startsWith('http://') || decodedText.startsWith('https://')) {
                  setQrMenuUrl(decodedText);
                  handleAnalyzeMenuUrl(decodedText);
                } else {
                  setMenuTextQuery(decodedText);
                  handleAnalyzeMenuTextQuery(decodedText);
                }
              }
            },
            () => {}
          );
        } catch (err) {
          console.error("Failed to start embedded QR scanner:", err);
          toast.error(lang === 'ua' ? 'Не вдалося відкрити камеру QR-сканера' : 'Failed to launch QR camera');
          setIsLiveQrActive(false);
        }
      }, 100);
    }
  };

  const handleQrUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsAnalyzingMenu(true);
      const { Html5Qrcode } = await import('html5-qrcode');
      const tempId = "temp-qr-file-scanner";
      let tempDiv = document.getElementById(tempId);
      if (!tempDiv) {
        tempDiv = document.createElement("div");
        tempDiv.id = tempId;
        tempDiv.style.display = "none";
        document.body.appendChild(tempDiv);
      }
      const html5QrCode = new Html5Qrcode(tempId);
      const decodedText = await html5QrCode.scanFile(file, false);
      if (decodedText) {
        toast.success(lang === 'ua' ? 'QR-код з зображення зчитано!' : 'QR Code extracted from image!');
        if (decodedText.startsWith('http://') || decodedText.startsWith('https://')) {
          setQrMenuUrl(decodedText);
          await handleAnalyzeMenuUrl(decodedText);
        } else {
          setMenuTextQuery(decodedText);
          await handleAnalyzeMenuTextQuery(decodedText);
        }
        setIsAnalyzingMenu(false);
      } else {
        throw new Error("No QR code found");
      }
    } catch (err) {
      // Fallback to analyzing photo directly
      console.warn("QR code not found in file, attempting photo AI analysis:", err);
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const rawBase64 = event.target?.result as string;
          if (!rawBase64) throw new Error("Empty image payload");
          const optimized = await resizeImage(rawBase64, 1280, 0.85);
          setScannedMenuImage(optimized);
          const data = optimized.includes(',') ? optimized.split(',')[1] : optimized;
          const result = await analyzeRestaurantMenu(data, activeDeficiencies, lang);
          if (result && Array.isArray(result.recommendations) && result.recommendations.length > 0) {
            setMenuRecommendations(result.recommendations);
            toast.success(lang === 'ua' ? 'Меню розпізнано за допомогою ШІ!' : 'Menu analyzed with AI!');
          } else {
            toast.error(lang === 'ua' ? 'QR-код не виявлено та не знайдено страв на фото.' : 'No QR code or dishes found on image.', { icon: '⚠️' });
          }
        } catch (photoErr) {
          console.error("Failed photo analysis fallback:", photoErr);
          toast.error(lang === 'ua' ? 'Помилка розпізнавання меню' : 'Failed to analyze menu');
        } finally {
          setIsAnalyzingMenu(false);
        }
      };
      reader.onerror = () => {
        setIsAnalyzingMenu(false);
        toast.error(lang === 'ua' ? 'Помилка читання файлу' : 'Failed to read file');
      };
      reader.readAsDataURL(file);
      e.target.value = '';
      return;
    }
    e.target.value = '';
  };

  const handleAnalyzeMenuUrl = async (urlToAnalyze?: string) => {
    const targetUrl = urlToAnalyze || qrMenuUrl;
    if (!targetUrl.trim()) return;
    setIsAnalyzingMenu(true);
    try {
      const result = await analyzeRestaurantMenuText(`Restaurant Menu Quick Link / URL: ${targetUrl.trim()}`, activeDeficiencies, lang);
      if (result && Array.isArray(result.recommendations) && result.recommendations.length > 0) {
        setMenuRecommendations(result.recommendations);
        toast.success(lang === 'ua' ? 'ШІ проаналізував меню за посиланням!' : 'AI analyzed restaurant menu link!');
      } else {
        toast.error(lang === 'ua' ? 'Не вдалося витягти страви з посилання' : 'Could not extract dishes from link');
      }
    } catch (err) {
      console.error("Error analyzing menu URL:", err);
      toast.error(lang === 'ua' ? 'Помилка аналізу посилання' : 'Failed to analyze link');
    } finally {
      setIsAnalyzingMenu(false);
    }
  };

  const handleAnalyzeMenuTextQuery = async (queryText?: string) => {
    const targetQuery = queryText || menuTextQuery;
    if (!targetQuery.trim()) return;
    setIsAnalyzingMenu(true);
    try {
      const result = await analyzeRestaurantMenuText(targetQuery.trim(), activeDeficiencies, lang);
      if (result && Array.isArray(result.recommendations) && result.recommendations.length > 0) {
        setMenuRecommendations(result.recommendations);
        toast.success(lang === 'ua' ? 'Страви проаналізовано! Перегляньте рекомендації.' : 'Menu analyzed! Check recommendations below.', { icon: '✨' });
      } else {
        toast.error(lang === 'ua' ? 'Не вдалося розпізнати страви з тексту' : 'Could not recognize dishes from text');
      }
    } catch (err) {
      console.error("Error analyzing menu text query:", err);
      toast.error(lang === 'ua' ? 'Помилка аналізу тексту' : 'Failed to analyze text');
    } finally {
      setIsAnalyzingMenu(false);
    }
  };

  const handleMenuUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsAnalyzingMenu(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const rawBase64 = event.target?.result as string;
        if (!rawBase64) throw new Error("Empty image payload");
        // Resize to 1280px at 0.85 quality: preserves sharp text for menus while keeping payload small and fast
        const optimized = await resizeImage(rawBase64, 1280, 0.85);
        setScannedMenuImage(optimized);
        const data = optimized.includes(',') ? optimized.split(',')[1] : optimized;
        const result = await analyzeRestaurantMenu(data, activeDeficiencies, lang);
        if (result && Array.isArray(result.recommendations) && result.recommendations.length > 0) {
          setMenuRecommendations(result.recommendations);
          toast.success(lang === 'ua' ? 'Меню проскановано! Знайдено найкращі страви.' : 'Menu scanned! Top meals identified.', { icon: '🍽️' });
        } else {
          toast.error(lang === 'ua' ? 'Не вдалося розпізнати страви з меню. Спробуйте чіткіше фото.' : 'Could not identify meals from menu photo. Please try a clearer photo.', { icon: '⚠️' });
        }
      } catch (err) {
        console.error("Error analyzing menu upload:", err);
        toast.error(lang === 'ua' ? 'Помилка аналізу фото меню' : 'Failed to analyze menu photo');
      } finally {
        setIsAnalyzingMenu(false);
      }
    };
    reader.onerror = () => {
      setIsAnalyzingMenu(false);
      toast.error(lang === 'ua' ? 'Помилка читання файлу' : 'Failed to read file');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleAnalyzeMenuText = async () => {
    await handleAnalyzeMenuTextQuery();
  };

  const handleDeleteRecipe = (index: number) => {
    const recipeName = recipeHistory[index]?.meal_name || '';
    setRecipeHistory(prev => {
      const newHistory = [...prev];
      newHistory.splice(index, 1);
      import("../services/localDbService").then(({ localDb }) => localDb.setItem("recipe_history", JSON.stringify(newHistory)));
      return newHistory;
    });
    setOpenRecipes(prev => {
      const newOpen: Record<string, boolean> = {};
      Object.entries(prev).forEach(([k, v]) => {
        if (k.startsWith('history-')) {
          const idx = parseInt(k.replace('history-', ''), 10);
          if (idx < index) {
            newOpen[k] = v;
          } else if (idx > index) {
            newOpen[`history-${idx - 1}`] = v;
          }
        } else {
          newOpen[k] = v;
        }
      });
      return newOpen;
    });
    toast.success(
      lang === 'ua'
        ? `Рецепт "${recipeName}" видалено з історії!`
        : `Recipe "${recipeName}" deleted from history!`,
      { icon: '🗑️' }
    );
  };

  const handleParseRecipe = async () => {
    if (!recipeUrl) return;
    setIsParsingRecipe(true);
    try {
      const result = await parseRecipeFromUrl(recipeUrl, lang);
      if (result) {
        setRecipeHistory(prev => {
          const newHistory = [result, ...prev];
          import("../services/localDbService").then(({ localDb }) => localDb.setItem("recipe_history", JSON.stringify(newHistory)));
          return newHistory;
        });
        setOpenRecipes(prev => {
          const newOpen: Record<string, boolean> = { 'history-0': true };
          Object.entries(prev).forEach(([k, v]) => {
            if (k.startsWith('history-')) {
              const idx = parseInt(k.replace('history-', ''), 10);
              newOpen[`history-${idx + 1}`] = v;
            } else {
              newOpen[k] = v;
            }
          });
          return newOpen;
        });
        setRecipeUrl('');
        toast.success(lang === 'ua' ? 'Рецепт розпізнано та збережено в історію!' : 'Recipe parsed and saved to history!', { icon: '📖' });
      } else {
        toast.error(lang === 'ua' ? 'Не вдалося розпізнати рецепт за цим посиланням' : 'Failed to parse recipe from this link');
      }
    } catch (e) {
      console.error(e);
      toast.error(lang === 'ua' ? 'Помилка під час розпізнавання рецепта' : 'Error parsing recipe');
    } finally {
      setIsParsingRecipe(false);
    }
  };

  const handleAddRecipeIngredientsToGrocery = (recipe: RecipeDetails) => {
    if (!recipe.ingredients || recipe.ingredients.length === 0) return;
    
    const ingredientsToAdd = recipe.ingredients.map(ing => ({
      name: ing.name,
      amount: ing.amount,
      source_meal_id: `recipe-${recipe.meal_name}`
    }));

    if (onAddMultipleItems) {
      onAddMultipleItems(ingredientsToAdd);
    } else {
      ingredientsToAdd.forEach(ing => onAddItem(ing.name, ing.amount));
    }

    toast.success(
      lang === 'ua'
        ? `Додано ${ingredientsToAdd.length} інгредієнтів з "${recipe.meal_name}" до списку продуктів!`
        : `Added ${ingredientsToAdd.length} ingredients from "${recipe.meal_name}" to your grocery list!`,
      { icon: '🛒' }
    );
  };

  // Grocery Planner Collapsible State (Closed by default)
  const [isGroceryPlannerExpanded, setIsGroceryPlannerExpanded] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Smart Grocery Generator State
  const [isGeneratorExpanded, setIsGeneratorExpanded] = useState(false);
  const [selectedOptionIndices, setSelectedOptionIndices] = useState<Record<string, number>>({});
  const [excludedIngredientNames, setExcludedIngredientNames] = useState<Set<string>>(new Set());

  // Macro Deficiency Sync State
  const [isMacroSyncExpanded, setIsMacroSyncExpanded] = useState(false);

  // Store Locator State
  const [isLocatorOpen, setIsLocatorOpen] = useState(false);
  const [locatorQuery, setLocatorQuery] = useState('');

  // Nutri-Agent State
  const [isNutriAgentConfigOpen, setIsNutriAgentConfigOpen] = useState(false);
  const [isNutriAgentActive, setIsNutriAgentActive] = useState(false);
  const [isNutriAgentApprovalOpen, setIsNutriAgentApprovalOpen] = useState(false);
  const [nutriAgentConfig, setNutriAgentConfig] = useState<NutriAgentLaunchConfig | null>(null);
  const [nutriAgentInitialMode, setNutriAgentInitialMode] = useState<GroceryListMode>('include_grocery');
  const [selectedOrderForDetail, setSelectedOrderForDetail] = useState<NutriAgentOrderRecord | null>(null);
  const [isNutriAgentHistoryExpanded, setIsNutriAgentHistoryExpanded] = useState(false);

  const handleDeleteNutriAgentOrder = (orderId: string) => {
    setNutriAgentHistory(prev => {
      const updated = prev.filter(item => (item.id || `${item.timestamp}`) !== orderId);
      import("../services/localDbService").then(({ localDb }) => localDb.setItem("nutri_agent_history", JSON.stringify(updated)));
      return updated;
    });
    setSelectedOrderForDetail(null);
  };

  const handleReorderNutriAgentOrder = (order: NutriAgentOrderRecord) => {
    setSelectedOrderForDetail(null);
    const storeName = (order.rawStore || order.store || 'silpo').toLowerCase();
    const resolvedStore = (['silpo', 'novus', 'atb', 'auchan', 'varus', 'metro', 'fora', 'tavriav', 'ultramarket', 'furshet'].includes(storeName)
      ? storeName
      : 'silpo') as any;

    const reorderConfig: NutriAgentLaunchConfig = {
      budgetLimitUSD: order.total > 0 ? Math.round(order.total * 1.15) : 50,
      currency: order.currency || (lang === 'ua' ? 'UAH' : 'USD'),
      currencySymbol: order.currencySymbol || (order.currency === 'UAH' ? '₴' : (lang === 'ua' ? '₴' : '$')),
      targetStores: [resolvedStore],
      messengerPlatform: ['viber', 'telegram'],
      notificationChannels: ['in_app'],
      userNotes: order.deliveryNotes || '',
      groceryListMode: 'include_grocery',
      fulfillmentMethod: /самовивіз|pickup/i.test(order.deliveryAddress || '') ? 'pickup' : 'delivery',
      groceryItems: order.itemDetails && order.itemDetails.length > 0
        ? order.itemDetails.map(it => ({
            name: it.name,
            amount: it.qty ? `${it.qty} ${it.unit || ''}`.trim() : undefined,
            unit: it.unit,
            checked: false
          }))
        : items.filter(i => !i.checked)
    };
    setNutriAgentConfig(reorderConfig);
    setIsNutriAgentActive(true);
    toast.success(
      lang === 'ua'
        ? `Повторне замовлення з ${order.store} запущено!`
        : `Re-order from ${order.store} initiated!`,
      { icon: '🔁' }
    );
  };

  useEffect(() => {
    const handleCollapse = () => {
      setIsGeneratorExpanded(false);
      setIsMacroSyncExpanded(false);
      setIsLocatorOpen(false);
      setIsNutriAgentHistoryExpanded(false);
    };
    window.addEventListener('collapse-all-spoilers', handleCollapse);
    return () => window.removeEventListener('collapse-all-spoilers', handleCollapse);
  }, []);

  const [userUnselectedMacroFoods, setUserUnselectedMacroFoods] = useState<Set<string>>(new Set());

  const macroDeficiencies = useMemo(() => {
    return activeDeficiencies; // Include all deficiency alerts (both macro and micro nutrients)
  }, [activeDeficiencies]);

  const selectedMacroFoods = useMemo(() => {
    const selected = new Set<string>();
    macroDeficiencies.forEach(def => {
      def.recommendedFoods.forEach(food => {
        const key = food.toLowerCase().trim();
        if (!userUnselectedMacroFoods.has(key)) {
          selected.add(key);
        }
      });
    });
    return selected;
  }, [macroDeficiencies, userUnselectedMacroFoods]);

  const handleToggleMacroFood = (food: string) => {
    const key = food.toLowerCase().trim();
    setUserUnselectedMacroFoods(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key); // Re-check
      } else {
        next.add(key); // Uncheck
      }
      return next;
    });
  };

  const handleAddMacroFoods = () => {
    const foodsToAdd: string[] = [];
    macroDeficiencies.forEach(def => {
      def.recommendedFoods.forEach(food => {
        const key = food.toLowerCase().trim();
        if (selectedMacroFoods.has(key)) {
          const isAlreadyInList = items.some(
            item => !item.checked && item.name.toLowerCase().trim() === key
          );
          if (!isAlreadyInList) {
            foodsToAdd.push(food);
          }
        }
      });
    });

    if (foodsToAdd.length === 0) {
      toast.info(
        lang === 'ua'
          ? 'Всі вибрані товари вже є у списку продуктів!'
          : 'All selected items are already on your grocery list!'
      );
      return;
    }

    if (onAddMultipleItems) {
      onAddMultipleItems(
        foodsToAdd.map(food => ({
          name: food.charAt(0).toUpperCase() + food.slice(1),
          source_meal_id: 'macro-defieciency-sync'
        }))
      );
    } else {
      foodsToAdd.forEach(food => {
        onAddItem(food.charAt(0).toUpperCase() + food.slice(1));
      });
    }

    toast.success(
      lang === 'ua'
        ? `Додано ${foodsToAdd.length} товарів для компенсації дефіциту!`
        : `Added ${foodsToAdd.length} items to address macro deficiencies!`,
      { icon: '🥗' }
    );
  };

  useEffect(() => {
    setExcludedIngredientNames(new Set());
  }, [plannedMeals]);

  const t = {
    title: lang === 'ua' ? 'КОНСЬЄРЖ' : 'CONCIERGE',
    addItem: lang === 'ua' ? 'Додати товар' : 'Add Item',
    placeholder: lang === 'ua' ? 'Наприклад: Куряче філе...' : 'e.g., Chicken Breast...',
    searchPlaceholder: lang === 'ua' ? 'Пошук товарів...' : 'Search items...',
    clearChecked: lang === 'ua' ? 'Видалити куплене' : 'Clear Checked',
    empty: lang === 'ua' ? 'Список порожній' : 'List is empty',
    emptyDesc: lang === 'ua' ? 'Додайте товари вручну або автоматично з рекомендацій ШІ' : 'Add items manually or automatically from AI suggestions',
    suggested: lang === 'ua' ? 'З рекомендацій' : 'From Suggestions',
  };

  const gt = {
    generatorTitle: lang === 'ua' ? 'ШІ генератор покупок' : 'Smart AI Grocery',
    generatorSubtitle: lang === 'ua' 
      ? 'Виділяє інгредієнти, яких бракує, з рекомендацій ШІ' 
      : 'Extracts missing ingredients from planned meal suggestions',
    selectPlannedMeals: lang === 'ua' ? 'Оберіть страви' : 'Select planned meals',
    missingIngredientsTitle: lang === 'ua' ? 'Інгредієнти, яких бракує:' : 'Missing ingredients:',
    addAllButton: lang === 'ua' ? 'Додати всі інгредієнти' : 'Add All to Grocery List',
    addAllMissing: (count: number) => lang === 'ua' 
      ? `Додати всі інгредієнти (${count})` 
      : `Add All Missing Ingredients (${count})`,
    addSelectedMissing: (count: number) => lang === 'ua' 
      ? `Додати вибрані інгредієнти (${count})` 
      : `Add Selected Ingredients (${count})`,
    alreadyHaveAll: lang === 'ua' 
      ? '🎉 Ви вже маєте всі інгредієнти для цих страв у списку!' 
      : '🎉 You already have all ingredients for these meals on your list!',
    excludeMeal: lang === 'ua' ? 'Пропустити' : 'Exclude',
    noPlannedMeals: lang === 'ua' 
      ? 'Немає активних рекомендацій від ШІ на сьогодні. Створіть їх на головному екрані!' 
      : 'No active AI meal suggestions for today. Generate some on the Home screen!',
    addedSuccess: lang === 'ua' 
      ? 'Інгредієнти додано до списку!' 
      : 'Ingredients added to list!',
    generateSuggestionsButton: lang === 'ua'
      ? 'Згенерувати рекомендації страв ШІ ⚡'
      : 'Generate AI Meal Suggestions ⚡',
    generatingSuggestionsText: lang === 'ua'
      ? 'AI створює рецепти та підбирає інгредієнти...'
      : 'AI is crafting recipes and selecting ingredients...',
  };

  const extractedIngredients = useMemo(() => {
    if (!plannedMeals || plannedMeals.length === 0) return [];

    const list: Array<{ name: string; amount?: string; unit?: string; source_meal_id?: string; meal_name: string }> = [];

    plannedMeals.forEach((group, groupIdx) => {
      const mealType = group.meal_type?.toLowerCase() || '';
      const selectedIdx = selectedOptionIndices[mealType] !== undefined ? selectedOptionIndices[mealType] : 0;
      
      if (selectedIdx === -1) return;

      const option = group.options && group.options[selectedIdx];
      if (!option) return;

      const mealId = `suggestion-${groupIdx}-${selectedIdx}`;

      if (option.ingredients && Array.isArray(option.ingredients) && option.ingredients.length > 0) {
        option.ingredients.forEach((ing: any) => {
          list.push({
            name: ing.name,
            amount: ing.amount !== undefined ? String(ing.amount) : undefined,
            unit: ing.unit || undefined,
            source_meal_id: mealId,
            meal_name: option.meal_name
          });
        });
      } else {
        list.push({
          name: option.meal_name,
          amount: undefined,
          unit: undefined,
          source_meal_id: mealId,
          meal_name: option.meal_name
        });
      }
    });

    return list;
  }, [plannedMeals, selectedOptionIndices]);

  const allExtractedMissing = useMemo(() => {
    const currentUncheckedNames = new Set(
      items.filter(i => !i.checked).map(i => i.name.toLowerCase().trim())
    );

    const uniqueMissing: Array<{ name: string; amount?: string; unit?: string; source_meal_id?: string; meal_name: string }> = [];
    const seenNames = new Set<string>();

    extractedIngredients.forEach(ing => {
      const key = ing.name.toLowerCase().trim();
      if (!currentUncheckedNames.has(key) && !seenNames.has(key)) {
        seenNames.add(key);
        uniqueMissing.push(ing);
      }
    });

    return uniqueMissing;
  }, [extractedIngredients, items]);

  const activeMissingIngredients = useMemo(() => {
    return allExtractedMissing.filter(ing => !excludedIngredientNames.has(ing.name.toLowerCase().trim()));
  }, [allExtractedMissing, excludedIngredientNames]);

  const isAllSelected = useMemo(() => {
    return excludedIngredientNames.size === 0;
  }, [excludedIngredientNames]);

  const handleToggleIngredientExclusion = (key: string) => {
    setExcludedIngredientNames(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleAddAllMissing = () => {
    if (activeMissingIngredients.length === 0) return;
    if (onAddMultipleItems) {
      onAddMultipleItems(activeMissingIngredients.map(ing => ({
        name: ing.name,
        amount: ing.amount,
        unit: ing.unit,
        source_meal_id: ing.source_meal_id
      })));
    } else {
      activeMissingIngredients.forEach(ing => {
        onAddItem(ing.name, ing.amount, ing.unit);
      });
    }
  };

  const filteredItems = useMemo(() => {
    return items.filter(item => 
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    ).sort((a, b) => {
      if (a.checked === b.checked) return b.timestamp - a.timestamp;
      return a.checked ? 1 : -1;
    });
  }, [items, searchQuery]);

  const handleAddItem = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newItemName.trim()) return;
    onAddItem(newItemName.trim());
    setNewItemName('');
    setIsAdding(false);
  };

  const checkedCount = items.filter(i => i.checked).length;

  const curatedRecipesList: RecipeDetails[] = useMemo(() => [
    {
      meal_name: lang === 'ua' ? 'Лососевий боул з кіноа та авокадо' : 'High-Protein Salmon Quinoa Bowl',
      prep_time: lang === 'ua' ? '15 хв' : '15 min',
      cook_time: lang === 'ua' ? '15 хв' : '15 min',
      difficulty: lang === 'ua' ? (vibe === 'casual' ? 'На ізі' : 'Легко') : (vibe === 'casual' ? 'Easy peasy' : 'Easy'),
      calories: 520,
      ingredients: [
        { name: lang === 'ua' ? 'Філе лосося' : 'Salmon Fillet', amount: '200g' },
        { name: lang === 'ua' ? 'Варена кіноа' : 'Cooked Quinoa', amount: '1 cup' },
        { name: lang === 'ua' ? 'Авокадо' : 'Avocado', amount: '1/2' },
        { name: lang === 'ua' ? 'Свіжий шпинат' : 'Baby Spinach', amount: '50g' },
        { name: lang === 'ua' ? 'Оливкова олія' : 'Olive Oil', amount: '1 tbsp' }
      ],
      steps: lang === 'ua' ? (
        vibe === 'casual' ? [
          { step: 1, title: 'Запарюємо кіноа', detail: 'Відвари кіноа за інструкцією — це твоя поживна база.', emoji: '🍚' },
          { step: 2, title: 'Смажимо рибку', detail: 'Обсмаж лосось на пательні по 3-4 хв з кожного боку до апетитної скоринки.', emoji: '🐟' },
          { step: 3, title: 'Збираємо боул', detail: 'Виклади шпинат, кіноа, стиглий авокадо та соковитий лосось у красиву миску.', emoji: '🥗' }
        ] : [
          { step: 1, title: 'Підготовка', detail: 'Відваріть кіноа згідно з інструкцією на упаковці.', emoji: '🍚' },
          { step: 2, title: 'Обсмажування', detail: 'Обсмажте філе лосося по 3-4 хв з кожного боку до повної готовності.', emoji: '🐟' },
          { step: 3, title: 'Подача', detail: 'Викладіть у тарілку свіжий шпинат, кіноа, нарізаний авокадо та лосось.', emoji: '🥗' }
        ]
      ) : (
        vibe === 'casual' ? [
          { step: 1, title: 'Prep the grain', detail: 'Boil your quinoa per package instructions — easy carb base.', emoji: '🍚' },
          { step: 2, title: 'Sear the salmon', detail: 'Pan-sear the salmon fillet for 3-4 mins per side until golden.', emoji: '🐟' },
          { step: 3, title: 'Assemble the bowl', detail: 'Toss spinach, fluffy quinoa, creamy avocado slices, and top with salmon.', emoji: '🥗' }
        ] : [
          { step: 1, title: 'Preparation', detail: 'Cook quinoa according to package instructions.', emoji: '🍚' },
          { step: 2, title: 'Searing', detail: 'Pan-sear the salmon fillet for 3-4 minutes on each side until cooked through.', emoji: '🐟' },
          { step: 3, title: 'Plating', detail: 'Arrange fresh spinach, quinoa, sliced avocado, and salmon neatly in a bowl.', emoji: '🥗' }
        ]
      ),
      chef_tip: lang === 'ua' 
        ? (vibe === 'casual' ? 'Хлюпни зверху трохи свіжого лимонного соку та сипани кунжуту — смак просто топ! 🔥' : 'Заправте свіжим лимонним соком та насінням кунжуту перед подачею.') 
        : (vibe === 'casual' ? 'Drizzle with fresh lemon juice & sesame seeds — super fresh & delicious! 🔥' : 'Drizzle with fresh lemon juice & sesame seeds for optimal flavor.')
    },
    {
      meal_name: lang === 'ua' ? 'Шпинатно-сочевичний карі (для заліза)' : 'Iron-Rich Spinach & Lentil Curry',
      prep_time: lang === 'ua' ? '10 хв' : '10 min',
      cook_time: lang === 'ua' ? '20 хв' : '20 min',
      difficulty: lang === 'ua' ? (vibe === 'casual' ? 'Нормас' : 'Середньо') : (vibe === 'casual' ? 'Moderate' : 'Medium'),
      calories: 410,
      ingredients: [
        { name: lang === 'ua' ? 'Червона сочевиця' : 'Red Lentils', amount: '150g' },
        { name: lang === 'ua' ? 'Свіжий шпинат' : 'Fresh Spinach', amount: '100g' },
        { name: lang === 'ua' ? 'Кокосове молоко' : 'Coconut Milk', amount: '200ml' },
        { name: lang === 'ua' ? 'Часник та імбир' : 'Garlic & Ginger', amount: '2 cloves' },
        { name: lang === 'ua' ? 'Томатне пюре' : 'Tomato Puree', amount: '2 tbsp' }
      ],
      steps: lang === 'ua' ? (
        vibe === 'casual' ? [
          { step: 1, title: 'Ароматна основа', detail: 'Обсмаж подрібнений часник, імбир та спеції на пательні 1-2 хвилини.', emoji: '🧄' },
          { step: 2, title: 'Тушкуємо сочевицю', detail: 'Закинь сочевицю, томати, кокосове молоко та тушкуй 15 хв до ніжності.', emoji: '🍲' },
          { step: 3, title: 'Зелений фініш', detail: 'Вмішай свіжий шпинат і дай йому стати м\'яким.', emoji: '🌿' }
        ] : [
          { step: 1, title: 'Пасерування', detail: 'Обсмажте подрібнений часник, імбир та спеції до появи аромату.', emoji: '🧄' },
          { step: 2, title: 'Тушкування', detail: 'Додайте сочевицю, томатне пюре, кокосове молоко та тушкуйте 15 хвилин.', emoji: '🍲' },
          { step: 3, title: 'Завершення', detail: 'Вмішайте свіжий шпинат і готуйте ще 2 хвилини до розм\'якшення.', emoji: '🌿' }
        ]
      ) : (
        vibe === 'casual' ? [
          { step: 1, title: 'Aromatics first', detail: 'Sauté minced garlic, ginger, and curry spices for 1-2 mins until fragrant.', emoji: '🧄' },
          { step: 2, title: 'Simmer lentils', detail: 'Add red lentils, tomato puree, coconut milk, and simmer for 15 mins.', emoji: '🍲' },
          { step: 3, title: 'Wilt the greens', detail: 'Fold in fresh spinach until wilted and rich.', emoji: '🌿' }
        ] : [
          { step: 1, title: 'Sautéing', detail: 'Heat garlic, ginger, and spices in a pan until aromatic.', emoji: '🧄' },
          { step: 2, title: 'Simmering', detail: 'Add lentils, tomato puree, and coconut milk; simmer for 15 minutes.', emoji: '🍲' },
          { step: 3, title: 'Finishing', detail: 'Fold in fresh spinach until wilted and thoroughly incorporated.', emoji: '🌿' }
        ]
      ),
      chef_tip: lang === 'ua' 
        ? (vibe === 'casual' ? 'Нереально смачно закриває денну норму заліза та клітковини!' : 'Ця страва ефективно поповнює щоденний дефіцит заліза та рослинного білка.') 
        : (vibe === 'casual' ? 'Super tasty way to hit your daily iron and fiber goals in one go!' : 'Rich in bioavailable iron and dietary fiber to support metabolic health.')
    },
    {
      meal_name: lang === 'ua' ? 'Індичка з перцем та броколі (Low-Carb)' : 'Keto Turkey & Pepper Stir-Fry',
      prep_time: lang === 'ua' ? '10 хв' : '10 min',
      cook_time: lang === 'ua' ? '12 хв' : '12 min',
      difficulty: lang === 'ua' ? (vibe === 'casual' ? 'Швидко' : 'Легко') : (vibe === 'casual' ? 'Quick' : 'Easy'),
      calories: 380,
      ingredients: [
        { name: lang === 'ua' ? 'Фарш із грудки індички' : 'Ground Turkey Breast', amount: '250g' },
        { name: lang === 'ua' ? 'Солодкий перець' : 'Bell Peppers', amount: '2 pcs' },
        { name: lang === 'ua' ? 'Броколі' : 'Broccoli Florets', amount: '100g' },
        { name: lang === 'ua' ? 'Соєвий соус' : 'Soy Sauce', amount: '1 tbsp' }
      ],
      steps: lang === 'ua' ? (
        vibe === 'casual' ? [
          { step: 1, title: 'Смажимо індичку', detail: 'Обсмаж фарш індички на сильному вогні до гарної золотавої скоринки.', emoji: '🍗' },
          { step: 2, title: 'Хрусткі овочі', detail: 'Додай нарізаний перчик та суцвіття броколі, смаж ще 4-5 хв.', emoji: '🥦' },
          { step: 3, title: 'Соусний штрих', detail: 'Влий соєвий соус, перемішай і потримай на вогні ще 2 хвилини.', emoji: '🔥' }
        ] : [
          { step: 1, title: 'Обсмажування', detail: 'Обсмажте фарш із грудки індички на пательні до готовності.', emoji: '🍗' },
          { step: 2, title: 'Додавання овочів', detail: 'Додайте солодкий перець і суцвіття броколі, готуйте 5 хвилин.', emoji: '🥦' },
          { step: 3, title: 'Заправка', detail: 'Влийте соєвий соус, ретельно перемішайте та тушкуйте 2-3 хвилини.', emoji: '🔥' }
        ]
      ) : (
        vibe === 'casual' ? [
          { step: 1, title: 'Brown the turkey', detail: 'Sauté ground turkey breast over medium-high heat until golden.', emoji: '🍗' },
          { step: 2, title: 'Toss in veggies', detail: 'Add sliced bell peppers and broccoli florets, stir-fry 4-5 mins.', emoji: '🥦' },
          { step: 3, title: 'Sauce it up', detail: 'Pour in soy sauce, give it a quick toss, and simmer for 2 mins.', emoji: '🔥' }
        ] : [
          { step: 1, title: 'Browning', detail: 'Cook ground turkey breast in a skillet until fully browned.', emoji: '🍗' },
          { step: 2, title: 'Vegetable Addition', detail: 'Add bell peppers and broccoli florets, cooking for 5 minutes.', emoji: '🥦' },
          { step: 3, title: 'Sauce & Simmer', detail: 'Pour soy sauce over the mixture and simmer for 2-3 minutes.', emoji: '🔥' }
        ]
      ),
      chef_tip: lang === 'ua' 
        ? (vibe === 'casual' ? 'Чистий білок майже без вуглеводів — ідеально на вечір!' : 'Високий вміст нежирного протеїну при мінімальній кількості вуглеводів.') 
        : (vibe === 'casual' ? 'High protein with next to zero carbs — perfect quick dinner!' : 'High in lean protein and micronutrients with low net carbohydrates.')
    }
  ], [lang, vibe]);

  return (
    <div className="flex flex-col h-full bg-transparent p-4 sm:p-6 space-y-6">
      {/* Top Header */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-3">
          <div className="bg-emerald-500/10 p-2.5 rounded-2xl">
            <Bot className="w-6 h-6 text-emerald-500" />
          </div>
          <div>
            <h2 className="text-xl font-black uppercase tracking-widest text-[var(--text)]">
              {t.title}
            </h2>
            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
              {lang === 'ua' ? 'ВАШ ПЕРСОНАЛЬНИЙ АСИСТЕНТ' : 'YOUR PERSONAL ASSISTANT'}
            </p>
          </div>
        </div>
        
        {activeTab === 'groceries' && checkedCount > 0 && (
          <div className="flex items-center gap-2">
            <button 
              onClick={onClearChecked}
              className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
              title={t.clearChecked}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="px-2" id="ai-concierge-tabs">
        <div className="flex glass bg-white/60 dark:bg-zinc-900/60 backdrop-blur-xl border border-zinc-200/60 dark:border-zinc-800/60 rounded-2xl p-1 gap-1 shadow-sm overflow-x-auto no-scrollbar flex-nowrap">
          <button
            onClick={() => {
              setActiveTab('groceries');
              if (typeof window !== 'undefined' && (window.innerWidth <= 768 || (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 768px), (pointer: coarse)').matches))) {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
              }
            }}
            className={cn(
              "flex-1 min-w-[100px] flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-xl text-[10px] sm:text-[11px] font-black uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap shrink-0",
              activeTab === 'groceries' 
                ? "bg-[#10B981] text-black shadow-md shadow-[#10B981]/20 font-black" 
                : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/40 dark:hover:bg-zinc-800/40"
            )}
          >
            <ShoppingCart className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{skin === 'stalker' ? 'ПРИПАСИ' : skin === 'anime' ? (lang === 'ua' ? 'Бенто-Квест' : 'Bento Quest') : (lang === 'ua' ? 'Покупки' : 'Grocery')}</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('eating_out');
              if (typeof window !== 'undefined' && (window.innerWidth <= 768 || (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 768px), (pointer: coarse)').matches))) {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
              }
            }}
            className={cn(
              "flex-1 min-w-[95px] flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-xl text-[10px] sm:text-[11px] font-black uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap shrink-0",
              activeTab === 'eating_out' 
                ? "bg-gradient-to-r from-amber-500/30 via-orange-500/25 to-amber-500/30 border border-amber-500/50 text-amber-900 dark:text-amber-300 shadow-md font-black" 
                : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/40 dark:hover:bg-zinc-800/40"
            )}
          >
            <Utensils className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{skin === 'anime' ? (lang === 'ua' ? 'Ізакая / Ресторани' : 'Izakaya & Dining') : (lang === 'ua' ? 'Ресторани' : 'Dining Out')}</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('recipes');
              if (typeof window !== 'undefined' && (window.innerWidth <= 768 || (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 768px), (pointer: coarse)').matches))) {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
              }
            }}
            className={cn(
              "flex-1 min-w-[85px] flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-xl text-[10px] sm:text-[11px] font-black uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap shrink-0",
              activeTab === 'recipes' 
                ? "bg-gradient-to-r from-purple-500/30 via-fuchsia-500/25 to-purple-500/30 border border-purple-500/50 text-purple-900 dark:text-purple-300 shadow-md font-black" 
                : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/40 dark:hover:bg-zinc-800/40"
            )}
          >
            <FileText className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{skin === 'anime' ? (lang === 'ua' ? 'Грімуар Рецептів' : 'Recipe Grimoire') : (lang === 'ua' ? 'Рецепти' : 'Recipes')}</span>
          </button>
        </div>
      </div>

      {/* Tab 1: Groceries */}
      {activeTab === 'groceries' && (
        <>
          {/* Grocery Planner Card (Reference Mockup Style) */}
          <div className="px-2 mb-6">
            <div className={cn(
              "rounded-[32px] p-5 sm:p-7 border relative overflow-hidden transition-all shadow-2xl",
              skin === 'stalker'
                ? "bg-black/95 border-[#4ade80]/40 font-mono text-[#4ade80]"
                : skin === 'anime'
                  ? "bg-gradient-to-b from-[#1c1435]/90 to-[#120e28]/95 border-pink-400/40 shadow-[0_10px_35px_rgba(236,72,153,0.2)] text-white"
                  : theme === 'light'
                    ? "bg-white/90 backdrop-blur-2xl border-zinc-200/90 shadow-[0_20px_50px_rgba(0,0,0,0.06)] text-zinc-900"
                    : "bg-zinc-900/90 backdrop-blur-2xl border-zinc-700/60 shadow-[0_25px_60px_rgba(0,0,0,0.4)] text-zinc-50",
              illumination && "illumination-food"
            )}>
              {/* Top ambient glow */}
              <div className={cn(
                "absolute -top-20 left-1/2 -translate-x-1/2 w-4/5 h-36 blur-3xl pointer-events-none rounded-full",
                skin === 'anime' ? "bg-pink-500/20" : "bg-emerald-500/15"
              )} />

              {/* 1. Header: Shopping Cart + GROCERY PLANNER (Click to toggle expand/collapse) */}
              <div 
                onClick={() => setIsGroceryPlannerExpanded(!isGroceryPlannerExpanded)}
                className="flex items-center justify-between relative z-10 cursor-pointer select-none"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className={cn(
                    "p-2 rounded-xl border shrink-0",
                    skin === 'anime'
                      ? "bg-pink-500/20 border-pink-400/40 text-pink-400 shadow-[0_0_10px_rgba(236,72,153,0.4)]"
                      : "bg-emerald-500/15 border-emerald-500/30 text-emerald-500 dark:text-emerald-400"
                  )}>
                    <ShoppingCart className="w-5 h-5 stroke-[2.5]" />
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <h2 className={cn(
                      "text-base sm:text-lg font-black tracking-wider uppercase truncate",
                      skin === 'stalker' ? "text-[#4ade80]" : skin === 'anime' ? "text-pink-300 drop-shadow-[0_0_8px_rgba(236,72,153,0.5)]" : theme === 'light' ? "text-zinc-900" : "text-white"
                    )}>
                      {skin === 'stalker' ? 'ПРИПАСИ ТА ХАБАР' : skin === 'anime' ? (lang === 'ua' ? '『БЕНТО ТА ПРИПАСИ ДЛЯ КВЕСТУ』 🍱✨' : '『BENTO & QUEST SUPPLIES』 🍱✨') : (lang === 'ua' ? 'ПОКУПКИ' : 'GROCERY')}
                    </h2>
                    {items.length > 0 && (
                      <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500 border border-emerald-500/30 shrink-0 font-mono">
                        {checkedCount > 0 ? `${checkedCount}/${items.length}` : items.length}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {items.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLocatorQuery('');
                        setIsLocatorOpen(true);
                      }}
                      className="p-2 rounded-xl text-zinc-400 hover:text-emerald-500 hover:bg-emerald-500/10 transition-colors cursor-pointer"
                      title={lang === 'ua' ? 'Знайти на карті' : 'Find on Map'}
                    >
                      <Search className="w-4 h-4" />
                    </button>
                  )}
                  {checkedCount > 0 && (
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClearChecked();
                      }}
                      className="p-2 rounded-xl text-zinc-400 hover:text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                      title={t.clearChecked}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsGroceryPlannerExpanded(!isGroceryPlannerExpanded);
                    }}
                    className="p-2 rounded-xl text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 transition-colors cursor-pointer"
                    aria-label={isGroceryPlannerExpanded ? (lang === 'ua' ? 'Згорнути' : 'Collapse') : (lang === 'ua' ? 'Розгорнути' : 'Expand')}
                  >
                    <ChevronDown className={cn("w-5 h-5 transition-transform duration-300", isGroceryPlannerExpanded && "rotate-180")} />
                  </button>
                </div>
              </div>

              {/* Collapsible Content (Closed by default) */}
              <AnimatePresence initial={false}>
                {isGroceryPlannerExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                    className="overflow-hidden space-y-5 pt-5 border-t border-zinc-200/60 dark:border-zinc-800/80 mt-4 relative z-10"
                  >
                    {/* 2. Subheader Row: WEEKLY LIST + Date */}
                    <div className="flex items-center justify-between pt-1 border-b border-zinc-200/60 dark:border-zinc-800/80 pb-3 relative z-10">
                <span className={cn(
                  "text-xs font-black uppercase tracking-widest",
                  skin === 'stalker' ? "text-[#4ade80]" : theme === 'light' ? "text-zinc-800" : "text-zinc-200"
                )}>
                  {skin === 'stalker' ? 'ТИЖНЕВИЙ СПИСОК' : (lang === 'ua' ? 'ТИЖНЕВИЙ СПИСОК' : 'WEEKLY LIST')}
                </span>
                <span className="text-xs font-semibold text-zinc-400 dark:text-zinc-400">
                  {new Date().toLocaleDateString(lang === 'ua' ? 'uk-UA' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>

              {/* 3. Items Counter */}
              <div className="text-xs font-bold text-zinc-500 dark:text-zinc-400 tracking-wide relative z-10 flex items-center justify-between">
                <span>{lang === 'ua' ? `Товарів: ${items.length}` : `Items: ${items.length}`}</span>
                {checkedCount > 0 && (
                  <span className="text-[10px] font-bold text-emerald-500">
                    {lang === 'ua' ? `Куплено: ${checkedCount}/${items.length}` : `Completed: ${checkedCount}/${items.length}`}
                  </span>
                )}
              </div>

              {/* 4. Grocery Items List */}
              <div className="space-y-2.5 relative z-10 max-h-[420px] overflow-y-auto no-scrollbar pr-1">
                {items.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-zinc-200 dark:border-zinc-800 rounded-2xl p-6">
                    <ShoppingCart className="w-8 h-8 mx-auto mb-2 text-zinc-400 opacity-40" />
                    <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider">
                      {lang === 'ua' ? 'Список порожній' : 'Cart is empty'}
                    </p>
                    <p className="text-[10px] text-zinc-500 mt-1">
                      {lang === 'ua' ? 'Додайте товари нижче або згенеруйте з плану' : 'Add items below or generate from meal plan'}
                    </p>
                  </div>
                ) : (
                  <AnimatePresence initial={false}>
                    {filteredItems.map(item => {
                      const raw = item.name.trim();
                      const dashIndex = raw.indexOf(' - ');
                      const parenIndex = raw.indexOf(' (');
                      let mainName = raw;
                      let suffix = '';
                      if (dashIndex !== -1) {
                        mainName = raw.substring(0, dashIndex).trim();
                        suffix = raw.substring(dashIndex).trim();
                      } else if (parenIndex !== -1) {
                        mainName = raw.substring(0, parenIndex).trim();
                        suffix = raw.substring(parenIndex).trim();
                      }
                      const lower = raw.toLowerCase();
                      const warningKeywords = ['sugar', 'sodium', 'salt', 'fat', 'carb', 'calorie', 'allergen', 'allergy', 'gluten', 'lactose', 'caution', 'warning', 'цукор', 'натрій', 'жир', 'калорії', 'сіль'];
                      const hasWarning = warningKeywords.some(kw => lower.includes(kw)) || !!(item.source_meal_id && item.source_meal_id.includes('deficiency'));

                      return (
                        <motion.div
                          key={item.id}
                          layout
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          onPointerDown={(e) => handlePointerDown(e, item.id, () => onDeleteItem(item.id))}
                          onPointerUp={(e) => handlePointerUp(e, () => onToggleItem(item.id))}
                          onPointerMove={handlePointerMove}
                          onPointerCancel={handlePointerCancel}
                          className={cn(
                            "group flex items-center justify-between p-2.5 sm:p-3 rounded-2xl cursor-pointer select-none transition-all",
                            item.checked
                              ? "bg-zinc-100/40 dark:bg-zinc-800/25 opacity-90"
                              : theme === 'light'
                                ? "bg-zinc-50/70 hover:bg-zinc-100/90 border border-zinc-200/60"
                                : "bg-zinc-800/30 hover:bg-zinc-800/60 border border-zinc-700/30"
                          )}
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            {/* Checkbox (Solid Green Check, Green Outline, or Amber Outline) */}
                            <div className="shrink-0">
                              {item.checked ? (
                                <div className="w-5 h-5 rounded-md bg-emerald-500 flex items-center justify-center text-white font-black shadow-xs">
                                  <Check className="w-3.5 h-3.5 stroke-[3] text-white" />
                                </div>
                              ) : hasWarning ? (
                                <div className="w-5 h-5 rounded-md border-2 border-amber-600/80 hover:border-amber-500 bg-transparent flex items-center justify-center transition-all group-hover:scale-105" />
                              ) : (
                                <div className="w-5 h-5 rounded-md border-2 border-emerald-500/80 hover:border-emerald-400 bg-transparent flex items-center justify-center transition-all group-hover:scale-105" />
                              )}
                            </div>

                            {/* Item Text: Green for unchecked normal, White for checked and caution */}
                            <div className="min-w-0 flex-1 text-sm font-semibold tracking-tight">
                              <span className={cn(
                                item.checked
                                  ? (theme === 'light' ? "text-zinc-900 font-bold" : "text-zinc-100 font-bold")
                                  : hasWarning
                                    ? (theme === 'light' ? "text-zinc-900 font-bold" : "text-zinc-100 font-bold")
                                    : (theme === 'light' ? "text-emerald-700 font-bold" : "text-emerald-400 font-bold")
                              )}>
                                {mainName}
                              </span>
                              {suffix && (
                                <span className="text-amber-500 dark:text-amber-400 text-xs font-medium ml-1">
                                  {suffix}
                                </span>
                              )}
                              {(item.amount || item.unit) && (
                                <span className="ml-2 text-[10px] font-bold text-zinc-400 bg-zinc-200/50 dark:bg-zinc-700/50 px-1.5 py-0.5 rounded-md">
                                  {item.amount} {item.unit}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Action button on hover */}
                          <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onDeleteItem(item.id);
                              }}
                              className="p-1.5 text-zinc-400 hover:text-red-400 rounded-lg transition-colors cursor-pointer"
                              title={lang === 'ua' ? 'Видалити' : 'Delete'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                )}
              </div>

              {/* 5. Inline + Add Item+ */}
              <div className="relative z-10">
                <AnimatePresence mode="wait">
                  {!isAdding ? (
                    <button
                      onClick={() => setIsAdding(true)}
                      className="flex items-center gap-2 text-xs font-bold text-zinc-400 hover:text-emerald-400 py-2 px-1 transition-colors cursor-pointer"
                    >
                      <Plus className="w-4 h-4" />
                      <span>{lang === 'ua' ? '+ Додати товар+' : '+ Add Item+'}</span>
                    </button>
                  ) : (
                    <motion.form
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      onSubmit={handleAddItem}
                      className="flex items-center gap-2 bg-zinc-100 dark:bg-zinc-800/80 border border-emerald-500/40 p-1.5 rounded-2xl shadow-md"
                    >
                      <input
                        autoFocus
                        type="text"
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        placeholder={t.placeholder}
                        className="flex-1 bg-transparent border-none focus:outline-none px-3 py-1.5 text-xs text-[var(--text)] font-semibold"
                      />
                      <button
                        type="submit"
                        disabled={!newItemName.trim()}
                        className="p-2 bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold rounded-xl disabled:opacity-50 transition-all cursor-pointer"
                      >
                        <Plus className="w-4 h-4 text-black stroke-[3]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsAdding(false);
                          setNewItemName('');
                        }}
                        className="p-1.5 text-zinc-400 hover:text-zinc-200 cursor-pointer"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>

              {/* 6. Mini Bullet / Tree Preview */}
              {items.length > 0 && (
                <div className="space-y-2 pl-3 text-xs font-medium relative z-10 py-1">
                  {items.slice(0, 3).map((item, idx, arr) => {
                    const raw = item.name.trim();
                    const dashIndex = raw.indexOf(' - ');
                    const parenIndex = raw.indexOf(' (');
                    let mainName = raw;
                    let suffix = '';
                    if (dashIndex !== -1) {
                      mainName = raw.substring(0, dashIndex).trim();
                      suffix = raw.substring(dashIndex).trim();
                    } else if (parenIndex !== -1) {
                      mainName = raw.substring(0, parenIndex).trim();
                      suffix = raw.substring(parenIndex).trim();
                    }
                    const lower = raw.toLowerCase();
                    const warningKeywords = ['sugar', 'sodium', 'salt', 'fat', 'carb', 'calorie', 'allergen', 'allergy', 'gluten', 'lactose', 'caution', 'warning', 'цукор', 'натрій', 'жир', 'калорії', 'сіль'];
                    const hasWarning = warningKeywords.some(kw => lower.includes(kw)) || !!(item.source_meal_id && item.source_meal_id.includes('deficiency'));

                    return (
                      <div key={idx} className="relative flex items-center gap-2.5 pl-1">
                        {idx < arr.length - 1 && (
                          <span className="absolute left-[5px] top-[14px] w-[1px] h-[16px] border-l border-dotted border-zinc-500" />
                        )}
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 shrink-0" />
                        <span className={cn("truncate", hasWarning ? "text-zinc-300" : "text-zinc-400")}>
                          {mainName}
                          {suffix && <span className="text-amber-400 font-normal"> {suffix}</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* 7. Bottom Pill Button: Generate from Meal Plan */}
              <div className="pt-2 relative z-10">
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => {
                    if (plannedMeals && plannedMeals.length > 0) {
                      setIsGeneratorExpanded(!isGeneratorExpanded);
                    } else if (onFetchSuggestions) {
                      onFetchSuggestions();
                      setIsGeneratorExpanded(true);
                    } else {
                      setIsGeneratorExpanded(!isGeneratorExpanded);
                    }
                  }}
                  className="w-full bg-[#1b6b47] hover:bg-[#15593a] text-white font-bold text-xs sm:text-sm tracking-wide py-3.5 px-6 rounded-full shadow-lg shadow-emerald-950/30 transition-all flex items-center justify-center gap-2 cursor-pointer border border-emerald-500/20"
                >
                  <span>{lang === 'ua' ? 'ЗГЕНЕРУАТИ З ПЛАНУ' : 'GENERATE FROM PLAN'}</span>
                </motion.button>

                <AnimatePresence>
                  {isGeneratorExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="border-t border-zinc-200/50 dark:border-zinc-800/80 pt-4 mt-4 overflow-hidden space-y-4"
                    >
                      {isGeneratingSuggestions ? (
                        <div className="p-6 flex flex-col items-center justify-center text-center space-y-3">
                          <Loader2 className="w-7 h-7 text-emerald-500 animate-spin" />
                          <p className="text-xs font-bold uppercase tracking-widest text-zinc-400">
                            {gt.generatingSuggestionsText}
                          </p>
                        </div>
                      ) : !plannedMeals || plannedMeals.length === 0 ? (
                        <div className="p-4 text-center space-y-3">
                          <p className="text-xs font-semibold text-zinc-400 leading-relaxed max-w-sm mx-auto">
                            {gt.noPlannedMeals}
                          </p>
                          {onFetchSuggestions && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onFetchSuggestions();
                              }}
                              className="bg-emerald-500 hover:bg-emerald-400 font-black text-zinc-950 text-[10px] uppercase tracking-widest py-2.5 px-5 rounded-2xl inline-flex items-center gap-2 shadow-md cursor-pointer transition-all active:scale-95"
                            >
                              <Sparkles className="w-3.5 h-3.5" />
                              <span>{gt.generateSuggestionsButton}</span>
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <h4 className="text-[10px] font-black uppercase tracking-wider text-zinc-400">
                              {gt.selectPlannedMeals}
                            </h4>
                            <div className="space-y-2.5">
                              {plannedMeals.map((group: any, gIdx: number) => {
                                const mealType = group.meal_type?.toLowerCase() || '';
                                const selectedIdx = selectedOptionIndices[mealType] !== undefined ? selectedOptionIndices[mealType] : 0;
                                
                                return (
                                  <div key={gIdx} className="space-y-1.5">
                                    <div className="flex items-center gap-1.5 pl-1">
                                      <span className={cn(
                                        "w-1.5 h-1.5 rounded-full",
                                        mealType === 'breakfast' ? 'bg-amber-500' :
                                        mealType === 'lunch' ? 'bg-emerald-500' :
                                        mealType === 'dinner' ? 'bg-indigo-500' :
                                        'bg-pink-500'
                                      )} />
                                      <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
                                        {lang === 'ua' ? (
                                          mealType === 'breakfast' ? 'Сніданок' :
                                          mealType === 'lunch' ? 'Обід' :
                                          mealType === 'dinner' ? 'Вечеря' :
                                          'Перекус'
                                        ) : (
                                          mealType.charAt(0).toUpperCase() + mealType.slice(1)
                                        )}
                                      </span>
                                    </div>

                                    <div className="flex flex-wrap gap-1.5">
                                      {group.options.map((option: any, oIdx: number) => {
                                        const isSelected = selectedIdx === oIdx;
                                        return (
                                          <button
                                            key={oIdx}
                                            onClick={() => setSelectedOptionIndices(prev => ({ ...prev, [mealType]: oIdx }))}
                                            className={cn(
                                              "px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all border text-left truncate max-w-[160px] sm:max-w-[220px] cursor-pointer",
                                              isSelected 
                                                ? "bg-emerald-500 text-zinc-950 font-black border-transparent shadow-xs"
                                                : theme === 'light'
                                                  ? "bg-zinc-100 border-zinc-200 text-zinc-700 hover:bg-zinc-200"
                                                  : "bg-zinc-800/60 border-zinc-700/60 text-zinc-300 hover:bg-zinc-800"
                                            )}
                                            title={option.meal_name}
                                          >
                                            {option.meal_name}
                                          </button>
                                        );
                                      })}
                                      
                                      <button
                                        onClick={() => setSelectedOptionIndices(prev => ({ ...prev, [mealType]: -1 }))}
                                        className={cn(
                                          "px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all border cursor-pointer",
                                          selectedIdx === -1
                                            ? "bg-red-500 text-white border-transparent"
                                            : theme === 'light'
                                              ? "bg-red-50 border-red-100 text-red-500 hover:bg-red-100/50"
                                              : "bg-red-950/20 border-red-900/30 text-red-400 hover:bg-red-950/40"
                                        )}
                                      >
                                        {gt.excludeMeal}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          <div className="space-y-3 pt-3 border-t border-zinc-200/50 dark:border-zinc-800/80">
                            <h4 className="text-[10px] font-black uppercase tracking-wider text-zinc-400">
                              {gt.missingIngredientsTitle}
                            </h4>
                            {allExtractedMissing.length === 0 ? (
                              <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-500 bg-emerald-500/10 p-3 rounded-2xl text-center border border-emerald-500/20">
                                {gt.alreadyHaveAll}
                              </p>
                            ) : (
                              <div className="space-y-2">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[160px] overflow-y-auto no-scrollbar pr-1">
                                  {allExtractedMissing.map((ing, idx) => {
                                    const key = ing.name.toLowerCase().trim();
                                    const isExcluded = excludedIngredientNames.has(key);
                                    return (
                                      <div 
                                        key={idx}
                                        onClick={() => handleToggleIngredientExclusion(key)}
                                        className={cn(
                                          "p-2 rounded-xl border flex items-center justify-between text-[11px] font-bold transition-all cursor-pointer select-none",
                                          isExcluded 
                                            ? "opacity-40 bg-zinc-100/40 dark:bg-zinc-800/20 border-dashed border-zinc-300 dark:border-zinc-800"
                                            : theme === 'light'
                                              ? "bg-zinc-50 border-zinc-200/70 text-zinc-800 hover:bg-zinc-100"
                                              : "bg-zinc-800/40 border-zinc-700/50 text-zinc-200 hover:bg-zinc-800/70"
                                        )}
                                      >
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                          <div className="shrink-0">
                                            {!isExcluded ? (
                                              <div className="w-4 h-4 rounded-sm bg-emerald-500 flex items-center justify-center text-zinc-950 font-black">
                                                <Check className="w-3 h-3 stroke-[3]" />
                                              </div>
                                            ) : (
                                              <div className="w-4 h-4 rounded-sm border border-zinc-400" />
                                            )}
                                          </div>
                                          <span className={cn("truncate pr-1", isExcluded && "line-through text-zinc-400")}>
                                            {ing.name}
                                          </span>
                                        </div>
                                        {(ing.amount || ing.unit) && (
                                          <span className="shrink-0 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-500">
                                            {ing.amount} {ing.unit}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                
                                <button
                                  onClick={handleAddAllMissing}
                                  disabled={activeMissingIngredients.length === 0}
                                  className="bg-emerald-500 hover:bg-emerald-400 font-black text-zinc-950 disabled:opacity-50 text-[10px] uppercase tracking-widest py-3 px-5 rounded-2xl flex items-center justify-center gap-2 shadow-md transition-all active:scale-95 w-full mt-2 cursor-pointer"
                                >
                                  <Check className="w-4 h-4" />
                                  <span>
                                    {isAllSelected 
                                      ? gt.addAllMissing(activeMissingIngredients.length)
                                      : gt.addSelectedMissing(activeMissingIngredients.length)
                                    }
                                  </span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>

          {/* Nutri-Agent Fulfillment Banner */}
          <div className="px-2 mb-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                if (userTier !== 'premium' && userTier !== 'premium_pro') {
                  toast.info(
                    lang === 'ua'
                      ? 'Автономний ШІ Nutri-Agent доступний у тарифі PREMIUM! У тарифі PRO вам доступний Smart Grocery Planner.'
                      : 'Autonomous Nutri-Agent is a PREMIUM feature! You have the Smart Grocery Planner in PRO.'
                  );
                  if (onOpenSubscriptionModal) {
                    onOpenSubscriptionModal();
                  }
                  return;
                }
                setIsNutriAgentConfigOpen(true);
              }}
              className={cn(
                "w-full p-4 rounded-3xl flex flex-col items-center justify-center gap-2 relative overflow-hidden",
                "bg-zinc-950 border shadow-xl transition-colors group cursor-pointer",
                (userTier === 'premium' || userTier === 'premium_pro')
                  ? "border-zinc-800 hover:border-emerald-500/50"
                  : "border-purple-500/30 hover:border-purple-500/60"
              )}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/0 via-emerald-500/10 to-emerald-500/0 opacity-0 group-hover:opacity-100 transition-opacity" />
              {(userTier === 'premium' || userTier === 'premium_pro') ? (
                <Bot className="w-8 h-8 text-emerald-500" />
              ) : (
                <div className="relative">
                  <Bot className="w-8 h-8 text-purple-400 opacity-60" />
                  <Lock className="w-4 h-4 text-purple-400 absolute -top-1 -right-2" />
                </div>
              )}
              <div className="text-center relative z-10">
                <h3 className="text-sm font-bold text-zinc-50 tracking-wide uppercase">Nutri-Agent Auto-Restock</h3>
                <p className="text-xs font-mono text-emerald-400/90 mt-1 uppercase tracking-wider">
                  {(userTier === 'premium' || userTier === 'premium_pro') ? 'Premium Fulfillment' : (lang === 'ua' ? 'Ексклюзив PREMIUM' : 'PREMIUM Only')}
                </p>
              </div>
            </motion.button>

          </div>
          
          {nutriAgentHistory.length > 0 && (
            <div className="px-2 mb-4 space-y-2">
              <div 
                className={cn(
                  "flex items-center justify-between px-2 transition-colors rounded-xl cursor-pointer select-none",
                  theme === 'light' ? "hover:bg-zinc-100" : "hover:bg-zinc-800",
                  !isNutriAgentHistoryExpanded ? "py-3" : "py-1"
                )}
                onClick={() => setIsNutriAgentHistoryExpanded(prev => !prev)}
              >
                <div className="flex items-center gap-2">
                  <History className="w-4 h-4 text-emerald-500" />
                  <h4 className="text-xs font-black uppercase tracking-widest text-[var(--text)] select-none">
                    {lang === "ua" ? "Історія Nutri-Agent" : "Nutri-Agent History"}
                  </h4>
                  <span className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest">
                    ({nutriAgentHistory.length} {lang === "ua" ? "замовлень" : "orders"})
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className={cn(
                      "p-1.5 rounded-[12px] text-zinc-500 hover:text-[var(--text)] transition-colors cursor-pointer",
                      theme === 'light' ? "bg-zinc-100 hover:bg-zinc-200" : "bg-zinc-800 hover:bg-zinc-700"
                    )}
                    aria-label={isNutriAgentHistoryExpanded ? (lang === 'ua' ? 'Згорнути історію' : 'Collapse history') : (lang === 'ua' ? 'Розгорнути історію' : 'Expand history')}
                  >
                    {isNutriAgentHistoryExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <AnimatePresence initial={false}>
                {isNutriAgentHistoryExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-2 pt-0.5">
                      {nutriAgentHistory.map((order, idx) => (
                        <motion.div
                          key={order.id || idx}
                          whileHover={{ scale: 1.03, y: -2 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => setSelectedOrderForDetail(order)}
                          className="shrink-0 bg-[var(--card-bg)] border border-[var(--card-border)] hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-all rounded-2xl p-3 min-w-[155px] cursor-pointer shadow-xs relative group select-none"
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[9px] font-bold text-zinc-400">{new Date(order.timestamp).toLocaleDateString()}</span>
                            <span className="text-[11px] font-black text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5 rounded-md border border-emerald-500/20">
                              {order.currencySymbol || (order.currency === 'UAH' ? '₴' : (lang === 'ua' ? '₴' : '$'))}{order.total?.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-1">
                            <p className="text-xs font-black text-[var(--text)] truncate">{order.store}</p>
                            <ExternalLink className="w-3 h-3 text-zinc-400 group-hover:text-emerald-500 transition-colors shrink-0" />
                          </div>
                          <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-[var(--card-border)]/50">
                            <span className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider">{order.items} {lang === "ua" ? "товарів" : "items"}</span>
                            <span className="text-[9px] font-extrabold text-emerald-500/80 group-hover:text-emerald-400 uppercase tracking-widest flex items-center gap-0.5">
                              {lang === "ua" ? "Деталі" : "Details"} →
                            </span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          <div className="px-2">
            <div className={cn(
              "border rounded-[32px] overflow-hidden transition-all relative",
              theme === 'light' 
                ? "bg-white border-zinc-100 shadow-[0_15px_45px_rgba(0,0,0,0.04)]" 
                : "bg-[var(--card-bg)] border-[var(--card-border)] shadow-[0_20px_60px_rgba(0,0,0,0.2)]",
              illumination && "illumination-food"
            )}>
              <div 
                onClick={() => setIsMacroSyncExpanded(!isMacroSyncExpanded)}
                className="p-5 flex items-center justify-between cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="bg-amber-500/10 p-2 rounded-2xl shrink-0">
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="text-xs font-black uppercase tracking-widest text-[var(--text)] flex items-center gap-2">
                      {lang === 'ua' ? 'Макро-дефіцит' : 'Macro Deficiency Sync'}
                      {macroDeficiencies.length > 0 && (
                        <span className="bg-amber-500 text-black text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-widest">
                          {macroDeficiencies.length}
                        </span>
                      )}
                    </h3>
                    <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider mt-0.5 max-w-[200px] sm:max-w-none truncate">
                      {lang === 'ua' 
                        ? 'Синхронізація дефіцитних інгредієнтів із щоденних звітів' 
                        : 'Sync deficient ingredients from daily alerts'}
                    </p>
                  </div>
                </div>
                <button className="p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors">
                  {isMacroSyncExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
              </div>

              <AnimatePresence>
                {isMacroSyncExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="border-t border-[var(--card-border)] overflow-hidden"
                  >
                    {macroDeficiencies.length === 0 ? (
                      <div className="p-6 text-center space-y-2">
                        <p className="text-xs font-bold text-[#10B981] bg-[#10B981]/5 p-4 rounded-2xl border border-[#10B981]/15 leading-relaxed max-w-sm mx-auto uppercase tracking-wide">
                          {lang === 'ua'
                            ? '🌟 Усі макронутрієнти збалансовані! Дефіцитів у щоденних звітах не виявлено.'
                            : '🌟 All macro-nutrients are fully balanced! No deficiencies detected in daily alerts.'}
                        </p>
                      </div>
                    ) : (
                      <div className="p-5 space-y-4">
                        <div className="space-y-4">
                          {macroDeficiencies.map((alert, aIdx) => (
                            <div key={aIdx} className="space-y-2 pb-3 border-b border-[var(--card-border)] last:border-0 last:pb-0">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black uppercase tracking-widest text-amber-500 flex items-center gap-1.5">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                  {alert.nutrientName}
                                </span>
                                <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider">
                                  {alert.daysCount} {lang === 'ua' ? 'днів дефіциту' : 'days deficiency'}
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pr-1">
                                {alert.recommendedFoods.map((food, fIdx) => {
                                  const key = food.toLowerCase().trim();
                                  const isSelected = selectedMacroFoods.has(key);
                                  return (
                                    <div 
                                      key={fIdx}
                                      onClick={() => handleToggleMacroFood(food)}
                                      className={cn(
                                        "p-2.5 rounded-xl border flex items-center justify-between text-[11px] font-bold transition-all cursor-pointer select-none",
                                        !isSelected 
                                          ? "opacity-40 bg-zinc-100/50 dark:bg-zinc-800/10 border-dashed border-zinc-200 dark:border-zinc-800"
                                          : theme === 'light'
                                            ? "bg-zinc-50 border-zinc-100 text-zinc-700 hover:bg-zinc-100"
                                            : "bg-zinc-800/30 border-zinc-700/40 text-zinc-300 hover:bg-zinc-800/50"
                                      )}
                                    >
                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <div className="shrink-0">
                                          {isSelected ? (
                                            <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                                          ) : (
                                            <Circle className="w-4 h-4 text-zinc-400" />
                                          )}
                                        </div>
                                        <span className={cn("truncate pr-2", !isSelected && "line-through text-zinc-400")}>
                                          {food}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>

                        <button
                          onClick={handleAddMacroFoods}
                          disabled={selectedMacroFoods.size === 0}
                          className="bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white disabled:opacity-50 text-[10px] font-black uppercase tracking-widest py-3 px-6 rounded-2xl flex items-center justify-center gap-2 shadow-[0_8px_30px_rgba(16,185,129,0.2)] hover:shadow-[0_8px_30px_rgba(16,185,129,0.3)] transition-all transform hover:scale-[1.01] active:scale-[0.99] w-full mt-3 cursor-pointer border border-transparent"
                        >
                          <Plus className="w-4 h-4" />
                          <span>
                            {lang === 'ua'
                              ? `Додати інгредієнти дефіциту (${selectedMacroFoods.size})`
                              : `Add Deficient Ingredients (${selectedMacroFoods.size})`}
                          </span>
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <NutriAgentConfigModal
            isOpen={isNutriAgentConfigOpen}
            onClose={() => setIsNutriAgentConfigOpen(false)}
            onLaunch={(config) => {
              setNutriAgentConfig(config);
              setIsNutriAgentConfigOpen(false);
              setIsNutriAgentActive(true);
            }}
            lang={lang}
            theme={theme}
            illumination={illumination}
            userTier={userTier || 'premium'}
            groceryItems={items}
            initialGroceryListMode={nutriAgentInitialMode}
          />

          <NutriAgentProgress
            isActive={isNutriAgentActive}
            config={nutriAgentConfig}
            lang={lang}
            vibe={vibe}
            theme={theme}
            skin={skin}
            illumination={illumination}
            onCancel={() => setIsNutriAgentActive(false)}
            onComplete={() => {
              setIsNutriAgentActive(false);
              setIsNutriAgentApprovalOpen(true);
              toast.success(lang === 'ua' ? "Кошик Nutri-Agent готовий до перегляду!" : "Nutri-Agent cart ready for review!", { icon: '🛒' });
            }}
          />

          <NutriAgentApprovalModal
            isOpen={isNutriAgentApprovalOpen}
            onClose={() => setIsNutriAgentApprovalOpen(false)}
            config={nutriAgentConfig}
            lang={lang}
            theme={theme}
            onOrderConfirmed={(order) => {
              setNutriAgentHistory(prev => {
                const newHistory = [order, ...prev];
                import("../services/localDbService").then(({ localDb }) => localDb.setItem("nutri_agent_history", JSON.stringify(newHistory)));
                return newHistory;
              });
              setIsNutriAgentHistoryExpanded(true);
            }}
          />

          <NutriAgentOrderDetailModal
            isOpen={!!selectedOrderForDetail}
            onClose={() => setSelectedOrderForDetail(null)}
            order={selectedOrderForDetail}
            lang={lang}
            theme={theme}
            onDelete={handleDeleteNutriAgentOrder}
            onReorder={handleReorderNutriAgentOrder}
          />
        </>
      )}

      {/* Tab 2: Dining Out & Menu Scan */}
      {activeTab === 'eating_out' && (
        <div className="flex-1 flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500 overflow-y-auto no-scrollbar pb-24 px-2">
          {activeDeficiencies.length > 0 && (
            <div className="bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-amber-500/15 rounded-3xl border border-amber-500/30 shadow-md overflow-hidden transition-all">
              <button
                type="button"
                onClick={() => setIsDeficienciesExpanded(prev => !prev)}
                className="w-full p-4 flex items-center justify-between gap-3 text-left cursor-pointer hover:bg-amber-500/5 transition-colors"
                aria-expanded={isDeficienciesExpanded}
              >
                <div className="flex items-center gap-3">
                  <div className="bg-amber-500/20 border border-amber-500/40 text-amber-400 p-2 rounded-2xl shrink-0 font-black">
                    <ShieldAlert className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-widest text-[var(--text)]">
                      {lang === 'ua' ? 'Активні дефіцити' : 'Active Nutrient Deficiencies'}
                    </h4>
                    <p className="text-[10px] text-zinc-500 dark:text-zinc-400 font-medium">
                      {activeDeficiencies.length} {lang === 'ua' ? (activeDeficiencies.length === 1 ? 'нутрієнт вимагає уваги' : 'нутрієнтів вимагають уваги') : (activeDeficiencies.length === 1 ? 'nutrient needs attention' : 'nutrients need attention')}
                    </p>
                  </div>
                </div>
                <div className="p-1 text-zinc-400 shrink-0">
                  <ChevronDown className={cn("w-5 h-5 transition-transform duration-300", isDeficienciesExpanded && "rotate-180")} />
                </div>
              </button>
              <AnimatePresence>
                {isDeficienciesExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 pt-1 flex flex-wrap gap-1.5 border-t border-amber-500/15">
                      {activeDeficiencies.map((d, i) => (
                        <span key={i} className="text-[9px] font-extrabold uppercase tracking-widest bg-amber-500/20 text-amber-400 px-2.5 py-0.5 rounded-full border border-amber-500/30">
                          {d.nutrientName} ({d.daysCount}d)
                        </span>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          <button
            onClick={() => {
              setLocatorQuery('');
              setIsLocatorOpen(true);
            }}
            className="w-full py-4 px-6 rounded-[24px] bg-[var(--card-bg)] border border-dashed border-amber-500/30 text-amber-500 flex items-center justify-center gap-2 hover:bg-amber-500/10 transition-all group cursor-pointer mb-2"
          >
            <Search className="w-5 h-5 group-hover:scale-110 transition-transform" />
            <span className="text-xs font-black uppercase tracking-widest">{lang === 'ua' ? 'Знайти ресторани поруч' : 'Find nearby restaurants'}</span>
          </button>
          
          <div className="border border-amber-500/25 rounded-[32px] p-6 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent dark:from-amber-500/15 dark:via-orange-950/20 dark:to-zinc-900/60 relative overflow-hidden space-y-6 shadow-[0_8px_30px_rgb(249,115,22,0.06)]">
            <div className="absolute -top-12 -right-12 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
            
            {isAnalyzingMenu ? (
              <div className="flex flex-col items-center justify-center py-10 space-y-4 relative z-10">
                <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
                <p className="text-amber-500 font-black uppercase tracking-widest text-sm">
                  {lang === 'ua' ? 'ШІ аналізує меню та підбирає страви...' : 'AI Analyzing Menu & Closing Deficiencies...'}
                </p>
              </div>
            ) : (
              <div className="space-y-6 relative z-10">
                <div className="flex flex-col items-center text-center">
                  <div className="p-4 rounded-3xl bg-amber-500/10 border border-amber-500/20 mb-3 shadow-inner">
                    <Utensils className="w-8 h-8 text-amber-500" />
                  </div>
                  <h3 className="text-lg font-black uppercase tracking-widest text-[var(--text)] mb-1">
                    {lang === 'ua' ? 'Сканер меню та QR-кодів у ресторанах' : 'Scan Restaurant Menu & Table QR Codes'}
                  </h3>
                  <p className="text-xs text-zinc-400 font-medium max-w-md">
                    {lang === 'ua' 
                      ? 'Відскануйте QR-код на столику, завантажте фото меню або введіть посилання/назви страв. ШІ підбере страви для покриття ваших дефіцитів.' 
                      : 'Scan a table QR code, upload a menu photo, or enter a menu link / dish names. AI analyzes dishes to fill your nutrient gaps.'}
                  </p>
                </div>

                {isLiveQrActive && (
                  <div className="bg-zinc-950/90 rounded-2xl p-4 border border-amber-500/40 relative space-y-3">
                    <div className="flex items-center justify-between text-xs font-bold text-amber-400 uppercase tracking-widest">
                      <span className="flex items-center gap-2"><QrCode className="w-4 h-4 animate-pulse" /> Live QR Scanner</span>
                      <button onClick={handleToggleLiveQrScanner} className="text-zinc-400 hover:text-white cursor-pointer"><X className="w-4 h-4" /></button>
                    </div>
                    <div id="dining-qr-scanner" className="w-full h-56 bg-black rounded-xl overflow-hidden border border-amber-500/30"></div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button 
                    onClick={handleToggleLiveQrScanner}
                    className="bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/30 text-[var(--text)] font-black uppercase tracking-widest py-3.5 px-4 rounded-2xl flex items-center justify-center gap-2 text-xs transition-all shadow-sm cursor-pointer"
                  >
                    <QrCode className="w-4 h-4 text-amber-500" />
                    <span>{isLiveQrActive ? (lang === 'ua' ? 'Закрити QR-сканер' : 'Close QR Scanner') : (lang === 'ua' ? 'Сканувати QR-код' : 'Scan QR Code')}</span>
                  </button>

                  <input type="file" accept="image/*" capture="environment" className="hidden" ref={menuFileInputRef} onChange={handleMenuUpload} />
                  <input type="file" accept="image/*" className="hidden" ref={qrFileInputRef} onChange={handleQrUpload} />

                  <button 
                    onClick={() => menuFileInputRef.current?.click()}
                    className="bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/30 text-[var(--text)] font-black uppercase tracking-widest py-3.5 px-4 rounded-2xl flex items-center justify-center gap-2 text-xs transition-all shadow-sm cursor-pointer"
                  >
                    <Camera className="w-4 h-4 text-amber-500" />
                    <span>{lang === 'ua' ? 'Фото меню' : 'Scan Menu Photo'}</span>
                  </button>
                </div>

                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={qrMenuUrl}
                      onChange={(e) => setQrMenuUrl(e.target.value)}
                      placeholder={lang === 'ua' ? 'Вставте QR-посилання на меню (https://...)...' : 'Paste QR menu link (https://...)...'}
                      className="flex-1 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-2xl px-4 py-3 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-amber-500/50 text-[var(--text)]"
                    />
                    <button
                      onClick={() => handleAnalyzeMenuUrl()}
                      disabled={!qrMenuUrl.trim() || isAnalyzingMenu}
                      className="bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/30 text-[var(--text)] font-black uppercase tracking-widest px-4 py-3 rounded-2xl disabled:opacity-50 flex items-center gap-2 text-xs cursor-pointer"
                      title={lang === 'ua' ? 'Аналізувати посилання' : 'Analyze Link'}
                    >
                      <LinkIcon className="w-4 h-4 text-amber-500" />
                    </button>
                    {qrMenuUrl.startsWith('http') && (
                      <a
                        href={qrMenuUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 p-3 rounded-2xl flex items-center justify-center"
                        title="Open Menu Link"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={menuTextQuery}
                      onChange={(e) => setMenuTextQuery(e.target.value)}
                      placeholder={lang === 'ua' ? 'Наприклад: Стейк лосося, Салат Цезар, Паста з куркою...' : 'e.g. Grilled Salmon, Caesar Salad, Chicken Pasta...'}
                      className="flex-1 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-2xl px-4 py-3 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-amber-500/50 text-[var(--text)]"
                    />
                    <button
                      onClick={() => handleAnalyzeMenuTextQuery()}
                      disabled={!menuTextQuery.trim() || isAnalyzingMenu}
                      className="bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/30 text-[var(--text)] font-black uppercase tracking-widest px-4 py-3 rounded-2xl disabled:opacity-50 flex items-center gap-2 text-xs cursor-pointer"
                    >
                      <Send className="w-4 h-4 text-amber-500" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {menuRecommendations.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-black uppercase tracking-widest text-amber-500 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  {lang === 'ua' ? 'Рекомендовані страви ШІ' : 'AI Recommended Dishes'}
                </h3>
                <button 
                  onClick={() => {
                    setMenuRecommendations([]);
                    setScannedMenuImage(null);
                  }}
                  className="text-[10px] font-extrabold uppercase text-zinc-400 hover:text-amber-500 transition-colors cursor-pointer"
                >
                  {lang === 'ua' ? 'Скинути' : 'Reset'}
                </button>
              </div>

              {scannedMenuImage && (
                <div className="flex items-center gap-3 p-3 rounded-2xl bg-amber-500/5 border border-amber-500/20">
                  <img 
                    src={scannedMenuImage} 
                    alt="Scanned Menu" 
                    className="w-14 h-14 object-cover rounded-xl border border-amber-500/30 shrink-0" 
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-black text-[var(--text)] truncate">
                      {lang === 'ua' ? 'Фото меню проаналізовано' : 'Menu Photo Analyzed'}
                    </p>
                    <p className="text-[10px] text-zinc-400">
                      {lang === 'ua' ? `Знайдено ${menuRecommendations.length} рекомендацій` : `${menuRecommendations.length} dishes recommended`}
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {menuRecommendations.map((rec, i) => (
                  <div key={i} className="bg-[var(--card-bg)] p-5 rounded-3xl border border-amber-500/25 shadow-md hover:border-amber-500/50 transition-all space-y-3">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-base font-black text-[var(--text)]">{rec.meal_name}</h4>
                        <p className="text-xs text-zinc-400 mt-1 font-medium leading-relaxed">{rec.description}</p>
                      </div>
                      <span className="bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-black px-2.5 py-1 rounded-xl shrink-0 shadow-sm">
                        {rec.match_score}% MATCH
                      </span>
                    </div>

                    <div className="pt-2 border-t border-[var(--card-border)] flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {rec.fixes_deficiencies && rec.fixes_deficiencies.length > 0 ? (
                          <>
                            <span className="text-[9px] font-black uppercase tracking-wider text-zinc-500">
                              {lang === 'ua' ? 'Закриває:' : 'Fixes:'}
                            </span>
                            {rec.fixes_deficiencies.map(def => (
                              <span key={def} className="text-[9px] font-black uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Check className="w-3 h-3" /> {def}
                              </span>
                            ))}
                          </>
                        ) : (
                          <span className="text-[9px] font-semibold text-zinc-500">
                            {lang === 'ua' ? 'Збалансована страва' : 'Nutritionally balanced'}
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => {
                          onAddItem(rec.meal_name);
                          toast.success(lang === 'ua' ? `«${rec.meal_name}» додано до списку!` : `Added "${rec.meal_name}" to grocery list!`);
                        }}
                        className="text-[10px] font-bold text-amber-500 hover:text-amber-400 flex items-center gap-1 shrink-0 px-2.5 py-1 rounded-xl bg-amber-500/10 border border-amber-500/20 transition-all cursor-pointer"
                        title={lang === 'ua' ? 'Додати до списку покупок' : 'Add to grocery list'}
                      >
                        <Plus className="w-3 h-3" />
                        <span>{lang === 'ua' ? 'В список' : 'To List'}</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Recipe Book */}
      {activeTab === 'recipes' && (
        <div className="flex-1 flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500 overflow-y-auto no-scrollbar pb-24 px-2">
          <div className="border border-purple-500/25 rounded-[32px] p-6 bg-gradient-to-br from-purple-500/10 via-fuchsia-500/5 to-transparent dark:from-purple-900/20 dark:via-fuchsia-950/15 dark:to-zinc-900/60 relative overflow-hidden space-y-4 shadow-[0_8px_30px_rgb(168,85,247,0.06)]">
            <div className="absolute -top-12 -right-12 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
            
            <h3 className="text-sm font-black uppercase tracking-widest text-[var(--text)] flex items-center gap-2 relative z-10">
              <LinkIcon className="w-4 h-4 text-purple-400" />
              {lang === 'ua' ? 'ШІ Парсер рецептів за URL' : 'AI Recipe URL Parser'}
            </h3>
            <div className="flex flex-col sm:flex-row gap-3 relative z-10">
              <input
                type="url"
                value={recipeUrl}
                onChange={(e) => setRecipeUrl(e.target.value)}
                placeholder={lang === 'ua' ? 'Вставте посилання на рецепт (TikTok, IG, Web)...' : 'Paste recipe link (TikTok, Instagram, Web)...'}
                className="flex-1 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-2xl px-4 py-3 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-purple-500/50 text-[var(--text)]"
              />
              <button 
                onClick={handleParseRecipe}
                disabled={!recipeUrl || isParsingRecipe}
                className="bg-gradient-to-r from-purple-500/20 via-fuchsia-500/20 to-purple-500/20 hover:from-purple-500/30 hover:to-fuchsia-500/30 border border-purple-500/30 text-[var(--text)] font-black uppercase tracking-widest px-6 py-3 rounded-2xl transition-all flex items-center justify-center gap-2 text-xs min-w-[120px] cursor-pointer disabled:opacity-50"
              >
                {isParsingRecipe ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === 'ua' ? 'Розпізнати' : 'Parse Recipe')}
              </button>
            </div>

            {parsedRecipe && (
              <div className="mt-4 bg-[var(--card-bg)] p-5 rounded-3xl border border-purple-500/30 space-y-4 animate-in fade-in duration-300 relative z-10">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-base font-black text-[var(--text)]">{parsedRecipe.meal_name}</h4>
                    <div className="flex gap-4 text-[10px] font-black text-purple-400 uppercase tracking-widest mt-1">
                      <span>⏱ {parsedRecipe.prep_time}</span>
                      <span>🔥 {parsedRecipe.cook_time}</span>
                      {parsedRecipe.calories && <span>⚡ {parsedRecipe.calories} kcal</span>}
                    </div>
                  </div>
                  <button onClick={() => setParsedRecipe(null)} className="text-[10px] font-extrabold text-zinc-400 hover:text-purple-400 uppercase cursor-pointer">
                    {lang === 'ua' ? 'Очистити' : 'Clear'}
                  </button>
                </div>

                <div className="space-y-2">
                  <h5 className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    {lang === 'ua' ? 'Інгредієнти:' : 'Ingredients:'}
                  </h5>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs font-semibold text-[var(--text)]">
                    {parsedRecipe.ingredients.map((ing, i) => (
                      <li key={i} className="flex items-center gap-2 bg-purple-500/10 px-3 py-1.5 rounded-xl border border-purple-500/15">
                        <Check className="w-3 h-3 text-purple-400 shrink-0" />
                        <span>{ing.name} {ing.amount && <span className="text-zinc-400 font-normal">({ing.amount})</span>}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => handleAddRecipeIngredientsToGrocery(parsedRecipe)}
                  className="w-full bg-gradient-to-r from-purple-500/20 via-fuchsia-500/20 to-purple-500/20 hover:from-purple-500/30 hover:to-fuchsia-500/30 border border-purple-500/30 text-[var(--text)] font-black uppercase tracking-widest py-3 px-6 rounded-2xl flex items-center justify-center gap-2 text-xs transition-all shadow-sm cursor-pointer"
                >
                  <ShoppingCart className="w-4 h-4 text-purple-400" />
                  <span className="whitespace-nowrap">{lang === 'ua' ? 'В СПИСОК ПОКУПОК' : 'ADD TO GROCERY LIST'}</span>
                </button>
              </div>
            )}
          </div>

          {/* Saved Recipes History */}
          {recipeHistory.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-black uppercase tracking-widest text-[var(--text)] flex items-center gap-2">
                  <History className="w-4 h-4 text-purple-400" />
                  {lang === 'ua' ? 'Історія рецептів' : 'Recipe History'}
                </h3>
                <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                  {recipeHistory.length} {lang === 'ua' ? 'рецептів' : 'recipes'}
                </span>
              </div>

              <div className="space-y-4">
                {recipeHistory.map((recipe, index) => {
                  const key = `history-${index}`;
                  const isOpen = !!openRecipes[key];
                  
                  return (
                    <motion.div
                      key={key}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className={cn(
                        "bg-[var(--card-bg)] border rounded-3xl p-5 hover:border-purple-500/40 transition-all space-y-4 shadow-sm select-none relative overflow-hidden",
                        isOpen ? "border-purple-500/30 ring-1 ring-purple-500/10" : "border-purple-500/20"
                      )}
                      onPointerDown={(e) => handlePointerDown(e, key, () => handleDeleteRecipe(index))}
                      onPointerUp={(e) => handlePointerUp(e, () => toggleRecipeOpen(key))}
                      onPointerMove={handlePointerMove}
                      onPointerCancel={handlePointerCancel}
                    >
                      <div className="flex justify-between items-start cursor-pointer">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-base font-black text-[var(--text)] truncate">{recipe.meal_name}</h4>
                            <span className="shrink-0 text-[8px] font-black uppercase tracking-widest text-red-500/60 bg-red-500/5 border border-red-500/10 px-1.5 py-0.5 rounded-md animate-pulse">
                              {lang === 'ua' ? 'Утримуйте для видалення' : 'Hold to delete'}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2.5 text-[10px] font-black text-purple-400 uppercase tracking-widest mt-1">
                            {recipe.prep_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {recipe.prep_time}</span>}
                            {recipe.calories && <span className="flex items-center gap-1"><Flame className="w-3 h-3" /> {recipe.calories} kcal</span>}
                            {recipe.difficulty && <span className="bg-purple-500/10 text-purple-400 border border-purple-500/20 px-2 py-0.5 rounded-full">{recipe.difficulty}</span>}
                          </div>
                        </div>
                        <div className="p-1 text-zinc-400">
                          {isOpen ? <ChevronUp className="w-4 h-4 text-purple-500" /> : <ChevronDown className="w-4 h-4 text-purple-400" />}
                        </div>
                      </div>

                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden space-y-4 pt-2 border-t border-[var(--card-border)]"
                          >
                            {recipe.chef_tip && (
                              <p className="text-xs text-zinc-400 italic bg-purple-500/5 p-3 rounded-2xl border border-purple-500/10">
                                💡 {recipe.chef_tip}
                              </p>
                            )}

                            <div className="space-y-2">
                              <h5 className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                                {lang === 'ua' ? 'Інгредієнти:' : 'Ingredients:'}
                              </h5>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                {recipe.ingredients && recipe.ingredients.map((ing, i) => (
                                  <div key={i} className="text-xs font-semibold text-[var(--text)] flex items-center justify-between bg-purple-500/10 border border-purple-500/15 px-3 py-1.5 rounded-xl">
                                    <span className="flex items-center gap-1.5">
                                      <Check className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                                      {ing.name}
                                    </span>
                                    {ing.amount && <span className="text-[10px] font-black text-purple-400 uppercase">{ing.amount}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>

                            {recipe.steps && recipe.steps.length > 0 && (
                              <div className="space-y-2">
                                <h5 className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                                  {lang === 'ua' ? 'Кроки приготування:' : 'Cooking Steps:'}
                                </h5>
                                <div className="space-y-2">
                                  {recipe.steps.map((step, sIdx) => (
                                    <div key={sIdx} className="bg-[var(--input-bg)] p-3 rounded-2xl border border-[var(--card-border)] flex gap-3 items-start">
                                      <div className="bg-purple-500/10 text-purple-400 text-xs font-black w-6 h-6 rounded-full flex items-center justify-center shrink-0">
                                        {step.emoji || step.step || (sIdx + 1)}
                                      </div>
                                      <div className="space-y-0.5">
                                        <p className="text-xs font-black text-[var(--text)]">{step.title}</p>
                                        <p className="text-[11px] text-zinc-400 leading-relaxed">{step.detail}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddRecipeIngredientsToGrocery(recipe);
                              }}
                              className="w-full bg-gradient-to-r from-purple-500/20 via-fuchsia-500/20 to-purple-500/20 hover:from-purple-500/30 hover:to-fuchsia-500/30 border border-purple-500/30 text-[var(--text)] font-black uppercase tracking-widest py-3 px-4 rounded-2xl flex items-center justify-center gap-2 text-xs transition-all shadow-sm hover:shadow-md cursor-pointer"
                            >
                              <Plus className="w-4 h-4 text-purple-400" />
                              <span className="whitespace-nowrap">{lang === 'ua' ? 'ДОДАТИ В АГЕНТ' : 'ADD TO NUTRI-AGENT'}</span>
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <h3 className="text-sm font-black uppercase tracking-widest text-[var(--text)] flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-purple-400" />
              {lang === 'ua' ? 'Рекомендована книга рецептів' : 'Curated Recipe Book'}
            </h3>

            <div className="space-y-4">
              {curatedRecipesList.map((recipe, index) => {
                const key = `curated-${index}`;
                const isOpen = !!openRecipes[key];
                
                return (
                  <motion.div 
                    key={key} 
                    className={cn(
                      "bg-[var(--card-bg)] border rounded-3xl p-5 hover:border-purple-500/40 transition-all space-y-4 shadow-sm select-none relative overflow-hidden",
                      isOpen ? "border-purple-500/30 ring-1 ring-purple-500/10" : "border-purple-500/20"
                    )}
                    onPointerDown={(e) => handlePointerDown(e, key, () => {})}
                    onPointerUp={(e) => handlePointerUp(e, () => toggleRecipeOpen(key))}
                    onPointerMove={handlePointerMove}
                    onPointerCancel={handlePointerCancel}
                  >
                    <div className="flex justify-between items-start cursor-pointer">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-base font-black text-[var(--text)] truncate">{recipe.meal_name}</h4>
                        <div className="flex flex-wrap gap-3 text-[10px] font-black text-purple-400 uppercase tracking-widest mt-1">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {recipe.prep_time}</span>
                          <span className="flex items-center gap-1"><Flame className="w-3 h-3" /> {recipe.calories} kcal</span>
                          <span className="bg-purple-500/10 text-purple-400 border border-purple-500/20 px-2 py-0.5 rounded-full">{recipe.difficulty}</span>
                        </div>
                      </div>
                      <div className="p-1 text-zinc-400">
                        {isOpen ? <ChevronUp className="w-4 h-4 text-purple-500" /> : <ChevronDown className="w-4 h-4 text-purple-400" />}
                      </div>
                    </div>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden space-y-4 pt-2 border-t border-[var(--card-border)]"
                        >
                          {recipe.chef_tip && (
                            <p className="text-xs text-zinc-400 italic bg-purple-500/5 p-3 rounded-2xl border border-purple-500/10">
                              💡 {recipe.chef_tip}
                            </p>
                          )}

                          <div className="space-y-2">
                            <h5 className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                              {lang === 'ua' ? 'Інгредієнти:' : 'Ingredients:'}
                            </h5>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                              {recipe.ingredients.map((ing, i) => (
                                <div key={i} className="text-xs font-semibold text-[var(--text)] flex items-center justify-between bg-purple-500/10 border border-purple-500/15 px-3 py-1.5 rounded-xl">
                                  <span className="flex items-center gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                                    {ing.name}
                                  </span>
                                  {ing.amount && <span className="text-[10px] font-black text-purple-400 uppercase">{ing.amount}</span>}
                                </div>
                              ))}
                            </div>
                          </div>

                          {recipe.steps && recipe.steps.length > 0 && (
                            <div className="space-y-2">
                              <h5 className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                                {lang === 'ua' ? 'Кроки приготування:' : 'Cooking Steps:'}
                              </h5>
                              <div className="space-y-2">
                                {recipe.steps.map((step, sIdx) => (
                                  <div key={sIdx} className="bg-[var(--input-bg)] p-3 rounded-2xl border border-[var(--card-border)] flex gap-3 items-start">
                                    <div className="bg-purple-500/10 text-purple-400 text-xs font-black w-6 h-6 rounded-full flex items-center justify-center shrink-0">
                                      {step.emoji || step.step || (sIdx + 1)}
                                    </div>
                                    <div className="space-y-0.5">
                                      <p className="text-xs font-black text-[var(--text)]">{step.title}</p>
                                      <p className="text-[11px] text-zinc-400 leading-relaxed">{step.detail}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddRecipeIngredientsToGrocery(recipe);
                            }}
                            className="w-full bg-gradient-to-r from-purple-500/20 via-fuchsia-500/20 to-purple-500/20 hover:from-purple-500/30 hover:to-fuchsia-500/30 border border-purple-500/30 text-[var(--text)] font-black uppercase tracking-widest py-3 px-4 rounded-2xl flex items-center justify-center gap-2 text-xs transition-all shadow-sm hover:shadow-md cursor-pointer"
                          >
                            <Plus className="w-4 h-4 text-purple-400" />
                            <span className="whitespace-nowrap">{lang === 'ua' ? 'В СПИСОК ПРОДУКТІВ' : 'ADD TO GROCERY LIST'}</span>
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <AnimatePresence>
        {isLocatorOpen && (
          <StoreLocator 
            isOpen={isLocatorOpen}
            onClose={() => setIsLocatorOpen(false)}
            initialQuery={locatorQuery}
            lang={lang}
            theme={theme}
            searchQuery={locatorQuery}
            items={items.filter(i => !i.checked)}
            mode={activeTab === 'eating_out' ? 'restaurant' : 'grocery'}
            deficiencies={activeDeficiencies}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
